import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getOrgDriverLicenceStatus } from "features/drivers/services/driverApi";
import { getDriverDisplayName } from "features/organizations/lib/orgDriverVehicles";
import { getMemberDisplayName } from "features/organizations/lib/orgMemberDisplay";
import {
  listOrgDrivers,
  listOrgMembers,
} from "features/organizations/services/organizationAdminApi";
import { queryKeys } from "core/query/queryKeys";
import { ROUTES } from "core/router/routes";
import { useProviderStore } from "core/store/providerStore";
import LicenceStatusGrid from "shared/components/provider/LicenceStatusGrid";
import OrgSwitcher from "shared/components/provider/OrgSwitcher";
import type { VerificationStatus, OrgDriver, OrgMember } from "types/api.generated";
import type { ApiError } from "types/api.types";

const VERIFICATION_BADGE: Record<string, string> = {
  unverified: "bg-slate-100 text-slate-700",
  pending: "bg-amber-50 text-amber-700",
  verified: "bg-emerald-50 text-emerald-700",
  rejected: "bg-red-50 text-red-700",
  expired: "bg-orange-50 text-orange-700",
};

export default function OrgDriverLicences() {
  const { userId = "" } = useParams<{ userId: string }>();
  const { activeOrgId } = useProviderStore();
  const orgId = activeOrgId ?? "";

  const driversQuery = useQuery<OrgDriver[]>({
    queryKey: queryKeys.organizations.drivers(orgId),
    queryFn: listOrgDrivers,
    enabled: Boolean(orgId),
  });

  const membersQuery = useQuery<OrgMember[]>({
    queryKey: queryKeys.organizations.members(orgId),
    queryFn: listOrgMembers,
    enabled: Boolean(orgId),
  });

  const driverProfile = useMemo(
    () => driversQuery.data?.find((d) => d.userId === userId),
    [driversQuery.data, userId],
  );

  const licenceQuery = useQuery({
    queryKey: queryKeys.drivers.orgLicenceStatus(orgId, userId),
    queryFn: () => getOrgDriverLicenceStatus(userId, orgId),
    enabled: Boolean(orgId && userId && Boolean(driverProfile)),
  });

  const memberProfile = useMemo(
    () => membersQuery.data?.find((m) => m.userId === userId),
    [membersQuery.data, userId],
  );

  const displayName = driverProfile
    ? getDriverDisplayName(driverProfile)
    : memberProfile
      ? getMemberDisplayName(memberProfile)
      : null;

  const email = driverProfile?.user.email ?? memberProfile?.user.email ?? null;

  const aggregateStatus = (licenceQuery.data?.verificationStatus ??
    driverProfile?.verificationStatus) as VerificationStatus | undefined;

  const licenceError = licenceQuery.error as ApiError | null;
  const isDisabledProfile =
    !driverProfile &&
    !driversQuery.isLoading &&
    Boolean(memberProfile || membersQuery.data);

  if (!orgId) {
    return (
      <p className="p-10 text-center text-slate-500">
        Select an organisation first.
      </p>
    );
  }

  return (
    <div className="min-h-screen bg-background-light px-6 py-10 font-display">
      <div className="max-w-lg mx-auto space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <Link
              to={ROUTES.PROVIDER.ORG_ADMIN}
              className="text-sm font-bold text-primary mb-2 inline-block"
            >
              ← Organisation
            </Link>
            <h1 className="text-2xl font-black">
              {displayName ?? "Driver licences"}
            </h1>
            {email && <p className="text-sm text-slate-500 mt-1">{email}</p>}
            {!displayName &&
              (driversQuery.isLoading || membersQuery.isLoading) && (
                <p className="text-sm text-slate-400 mt-1">Loading driver…</p>
              )}
            <p className="text-sm text-slate-600 mt-2">
              View licence progress by vehicle type. Platform admins approve
              submissions.
            </p>
          </div>
          {aggregateStatus && (
            <span
              className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full shrink-0 ${
                VERIFICATION_BADGE[aggregateStatus] ??
                VERIFICATION_BADGE.unverified
              }`}
            >
              {aggregateStatus.replace("_", " ")}
            </span>
          )}
        </div>
        <OrgSwitcher />

        {isDisabledProfile && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 space-y-2">
            <p>
              This person is not enabled as a driver for this organisation.
              Enable their driver profile first to view licence status.
            </p>
            <Link
              to={ROUTES.PROVIDER.ORG_ADMIN}
              className="font-bold text-primary"
            >
              Go to organisation settings
            </Link>
          </div>
        )}

        {licenceQuery.isLoading && driverProfile && (
          <p className="text-sm text-slate-500">Loading licence status…</p>
        )}

        {licenceQuery.isError && driverProfile && (
          <p className="text-sm text-red-600">
            {licenceError?.message ||
              "Could not load licence status. They may need to be enabled as a driver first."}
          </p>
        )}

        {licenceQuery.data && (
          <LicenceStatusGrid
            licences={licenceQuery.data.licences}
            variant="orgView"
          />
        )}
      </div>
    </div>
  );
}

